import { FC, useCallback, useEffect, useState } from 'react';

import { MdKeyboardArrowDown, MdOutlineCampaign } from 'react-icons/md';

import { useTheme } from 'styled-components';

import { Typography } from '~/common/components/Typography';
import { Modal2 } from '~/common/components/Modal2';
import { Collapse } from '~/common/components/Collapse';
import { Button } from '~/common/components/Button';

import {
  CurrentTag,
  EmptyVersions,
  Item,
  ItemBadge,
  ItemContent,
  ItemList,
  ModalBody,
  ModalFooter,
  VersionChevron,
  VersionHeader,
  VersionItem,
  VersionList,
  VersionTitleWrapper,
} from './styles';
import { TReleaseItemType } from '../../interfaces/i-release-notes';
import { currentVersion, releaseVersions } from '../../data';

interface IReleaseNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ITEM_LABEL: Record<TReleaseItemType, string> = {
  novidade: 'NOVIDADE',
  correcao: 'CORREÇÃO',
};

/**
 * Formata a data sem passar por `new Date`: a string vem em `YYYY-MM-DD`, que o
 * construtor interpreta como UTC e, em fuso negativo como o nosso, exibiria o
 * dia anterior.
 */
const formatReleaseDate = (isoDate: string): string => {
  const [year, month, day] = String(isoDate || '').split('-');

  if (!year || !month || !day) {
    return isoDate || '';
  }

  return `${day}/${month}/${year}`;
};

export const ReleaseNotesModal: FC<IReleaseNotesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const theme = useTheme();
  // Acordeão: uma versão aberta por vez, começando pela mais recente.
  const [openVersion, setOpenVersion] = useState<string | null>(
    releaseVersions[0]?.versao || null
  );

  // Cada reabertura volta ao estado inicial — quem clica no rodapé da sidebar
  // espera ver a versão atual expandida, não a que ficou aberta da última vez.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setOpenVersion(releaseVersions[0]?.versao || null);
  }, [isOpen]);

  const handleToggleVersion = useCallback((versao: string) => {
    setOpenVersion((current) => (current === versao ? null : versao));
  }, []);

  const getItemColor = useCallback(
    (tipo: TReleaseItemType): string =>
      tipo === 'novidade' ? theme.primary : theme.green,
    [theme]
  );

  return (
    <Modal2
      id="release-notes-modal"
      isOpen={isOpen}
      onClose={onClose}
      title="Novidades"
      iconTitle={<MdOutlineCampaign size={22} color={theme.primary} />}
      width="640px"
      maxHeight="85vh"
      justifyContent="flex-start"
    >
      <ModalBody>
        {releaseVersions.length === 0 ? (
          <EmptyVersions>
            <Typography.Paragraph size="small" color="neutral.500">
              Nenhuma novidade publicada ainda.
            </Typography.Paragraph>
          </EmptyVersions>
        ) : (
          <VersionList>
            {releaseVersions.map((version) => {
              const isVersionOpen = version.versao === openVersion;

              return (
                <VersionItem key={version.versao} $isOpen={isVersionOpen}>
                  <VersionHeader
                    type="button"
                    $isOpen={isVersionOpen}
                    onClick={() => handleToggleVersion(version.versao)}
                  >
                    <VersionTitleWrapper>
                      <Typography.Label
                        size="small"
                        color={isVersionOpen ? 'primary' : 'neutral.200'}
                      >
                        {`Versão ${version.versao}${
                          version.titulo ? ` — ${version.titulo}` : ''
                        }`}
                      </Typography.Label>

                      <Typography.Label size="x-small" color="neutral.500">
                        {formatReleaseDate(version.data)}
                      </Typography.Label>
                    </VersionTitleWrapper>

                    {version.versao === currentVersion && (
                      <CurrentTag>
                        <Typography.Label size="x-small" color="primary">
                          Atual
                        </Typography.Label>
                      </CurrentTag>
                    )}

                    <VersionChevron $isOpen={isVersionOpen}>
                      <MdKeyboardArrowDown
                        size={20}
                        color={
                          isVersionOpen ? theme.primary : theme.neutral[500]
                        }
                      />
                    </VersionChevron>
                  </VersionHeader>

                  <Collapse
                    isOpen={isVersionOpen}
                    hasBoxShadow={false}
                    hasMarginBottom={false}
                  >
                    <ItemList>
                      {(version.itens || []).map((item) => (
                        <Item key={`${version.versao}-${item.titulo}`}>
                          <ItemBadge $color={getItemColor(item.tipo)}>
                            <Typography.Label
                              size="x-small"
                              color="neutral.100"
                            >
                              {ITEM_LABEL[item.tipo] || item.tipo}
                            </Typography.Label>
                          </ItemBadge>

                          <ItemContent>
                            <Typography.Label size="small" color="neutral.200">
                              {item.titulo}
                            </Typography.Label>

                            {!!item.descricao && (
                              <Typography.Paragraph
                                size="x-small"
                                color="neutral.500"
                              >
                                {item.descricao}
                              </Typography.Paragraph>
                            )}
                          </ItemContent>
                        </Item>
                      ))}
                    </ItemList>
                  </Collapse>
                </VersionItem>
              );
            })}
          </VersionList>
        )}

        <ModalFooter>
          <Button
            type="button"
            text="Entendi"
            justifyContent="center"
            textOrIconColor={theme.neutral[1000]}
            onClick={onClose}
            width="140px"
          />
        </ModalFooter>
      </ModalBody>
    </Modal2>
  );
};
